// src/components/RegisterOrder.jsx
import React, { useState, useEffect } from 'react';
import Select from 'react-select';
import moment from 'moment-timezone';
import 'moment/locale/es';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import '../styles/RegisterOrder.css';

import api from '../api/api';
import { useNavigate } from 'react-router-dom';

const RegisterOrder = () => {
  const navigate = useNavigate();

  const [clients, setClients] = useState([]);

  const [formData, setFormData] = useState({
    customer: '',
    parcial: '',
    transporte: '',
    totalPayment: '',
    fuente: '',
    ejecutivo: '',
    telefono: '',
    para: '',
    entregarEn: '',
    detalleEntrega: '',
    iva: 'No',
    tarjetaOption: 'No',
    tarjetaDetalle: '',
    recibidoPor: '',
    hora: '',
    orderItems: '',
    formaPago: '',
  });

  const [deliveryDate, setDeliveryDate] = useState(null);
  const [computedIva, setComputedIva] = useState('0.00');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleUnauthorized = () => {
    localStorage.removeItem('token');
    navigate('/login');
  };

  useEffect(() => {
    const fetchClients = async () => {
      try {
        const res = await api.get('/api/clients');
        setClients(res.data || []);
      } catch (error) {
        console.error('Error al obtener clientes:', error);
        if (error?.response?.status === 401) return handleUnauthorized();
      }
    };
    fetchClients();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Recalcular total y computedIva
  useEffect(() => {
    const parcialValue = parseFloat(formData.parcial) || 0;
    const transporteValue = parseFloat(formData.transporte) || 0;
    const base = parcialValue + transporteValue;

    if (formData.iva === 'Si') {
      const ivaAmt = base * 0.13;
      setComputedIva(ivaAmt.toFixed(2));
      setFormData((prev) => ({
        ...prev,
        totalPayment: (base + ivaAmt).toFixed(2),
      }));
    } else {
      setComputedIva('0.00');
      setFormData((prev) => ({
        ...prev,
        totalPayment: base.toFixed(2),
      }));
    }
  }, [formData.parcial, formData.transporte, formData.iva]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;

    if (!deliveryDate) {
      alert('La fecha y hora de entrega son obligatorias.');
      return;
    }

    setIsSubmitting(true);

    try {
      const scheduledTimeCR = moment(deliveryDate)
        .tz('America/Costa_Rica')
        .format(); // ISO

      // ✅ total correcto para backend: el TOTAL FINAL (totalPayment)
      const totalFinal = parseFloat(formData.totalPayment || 0) || 0;

      const payload = {
        // si tu backend lo usa, no estorba:
        tipoOrden: 'NORMAL',

        customer: formData.customer || 'vacío',

        // ✅ IMPORTANTÍSIMO:
        // "total" es required en tu modelo, y lo correcto es el total final
        total: totalFinal,

        scheduledTime: scheduledTimeCR,

        fuente: formData.fuente || undefined,
        ejecutivo: formData.ejecutivo || undefined,
        telefono: formData.telefono || undefined,
        para: formData.para || undefined,
        entregarEn: formData.entregarEn || undefined,
        detalleEntrega: formData.detalleEntrega || undefined,

        // ✅ tarjeta separado en 2 campos (como el backend normalmente lo espera)
        tarjeta: formData.tarjetaOption || 'No',
        tarjetaDetalle: formData.tarjetaOption === 'Si' ? (formData.tarjetaDetalle || '') : '',

        recibidoPor: formData.recibidoPor || undefined,
        hora: formData.hora || undefined,
        orderItems: formData.orderItems || undefined,

        parcial: formData.parcial || undefined,
        transporte: formData.transporte || undefined,
        iva: formData.iva || 'No',

        totalPayment: formData.totalPayment || undefined,

        formaPago: formData.formaPago || undefined,
      };

      const res = await api.post('/api/orders', payload);

      alert('Pedido registrado con éxito: ' + (res?.data?.orderNumber || ''));

      setFormData({
        customer: '',
        parcial: '',
        transporte: '',
        totalPayment: '',
        fuente: '',
        ejecutivo: '',
        telefono: '',
        para: '',
        entregarEn: '',
        detalleEntrega: '',
        iva: 'No',
        tarjetaOption: 'No',
        tarjetaDetalle: '',
        recibidoPor: '',
        hora: '',
        orderItems: '',
        formaPago: '',
      });

      setDeliveryDate(null);
      setComputedIva('0.00');
    } catch (error) {
      console.error('Error al registrar el pedido:', error);

      if (error?.response?.status === 401) return handleUnauthorized();

      const msg =
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        'Error al registrar el pedido.';
      alert(msg);
    } finally {
      setIsSubmitting(false);
    }
  };

  const clientOptions = (clients || []).map((client) => ({
    value: client.name,
    label: client.name,
  }));

  return (
    <div className="register-order">
      <header className="ro-header">
        <h1>Registrar Nuevo Pedido</h1>
      </header>

      <form className="ro-form" onSubmit={handleSubmit}>
        <div className="ro-form-row">
          <div className="ro-form-group half">
            <label>Fuente:</label>
            <input
              type="text"
              name="fuente"
              placeholder="Ingrese la fuente"
              value={formData.fuente}
              onChange={handleChange}
            />
          </div>

          <div className="ro-form-group half">
            <label>Ejecutiv@:</label>
            <input
              type="text"
              name="ejecutivo"
              placeholder="Ingrese el nombre del ejecutivo"
              value={formData.ejecutivo}
              onChange={handleChange}
            />
          </div>
        </div>

        <div className="ro-form-row">
          <div className="ro-form-group half">
            <label>Para:</label>
            <input
              type="text"
              name="para"
              placeholder="Nombre del cliente o ingrese manualmente"
              value={formData.para}
              onChange={handleChange}
            />
          </div>

          <div className="ro-form-group half">
            <label>Teléfono:</label>
            <input
              type="text"
              name="telefono"
              placeholder="Ingrese el teléfono"
              value={formData.telefono}
              onChange={handleChange}
            />
          </div>
        </div>

        <div className="ro-form-group">
          <label>Fecha y Hora de Entrega:</label>
          <DatePicker
            selected={deliveryDate}
            onChange={(date) => setDeliveryDate(date)}
            showTimeSelect
            timeFormat="hh:mm aa"
            timeIntervals={15}
            dateFormat="dd/MM/yyyy h:mm aa"
            placeholderText="Seleccione fecha y hora"
            className="ro-datepicker-input"
          />
        </div>

        <div className="ro-form-group">
          <label>Detalle De Lo Entregado:</label>
          <textarea
            name="detalleEntrega"
            placeholder="Agregue detalles o productos entregados..."
            value={formData.detalleEntrega}
            onChange={handleChange}
          />
        </div>

        <div className="ro-form-group">
          <label>Entregar En:</label>
          <input
            type="text"
            name="entregarEn"
            placeholder="Lugar de entrega"
            value={formData.entregarEn}
            onChange={handleChange}
          />
        </div>

        <div className="ro-form-group">
          <label>Tarjeta (Si/No):</label>
          <select name="tarjetaOption" value={formData.tarjetaOption} onChange={handleChange}>
            <option value="No">No</option>
            <option value="Si">Si</option>
          </select>
        </div>

        {formData.tarjetaOption === 'Si' && (
          <div className="ro-form-group">
            <label>Detalles de la Tarjeta:</label>
            <input
              type="text"
              name="tarjetaDetalle"
              placeholder="Ingrese detalles de la tarjeta"
              value={formData.tarjetaDetalle}
              onChange={handleChange}
            />
          </div>
        )}

        <div className="ro-form-row">
          <div className="ro-form-group half">
            <label>Parcial:</label>
            <input
              type="text"
              name="parcial"
              placeholder="Monto parcial"
              value={formData.parcial}
              onChange={handleChange}
            />
          </div>

          <div className="ro-form-group half">
            <label>Transporte:</label>
            <input
              type="text"
              name="transporte"
              placeholder="Costo de transporte"
              value={formData.transporte}
              onChange={handleChange}
            />
          </div>
        </div>

        <div className="ro-form-group">
          <label>IVA (Si/No):</label>
          <select name="iva" value={formData.iva} onChange={handleChange}>
            <option value="No">No</option>
            <option value="Si">Si</option>
          </select>
        </div>

        {formData.iva === 'Si' && (
          <div className="ro-form-group">
            <label>IVA (13%):</label>
            <input type="text" name="computedIva" value={computedIva} readOnly />
          </div>
        )}

        <div className="ro-form-group">
          <label>TOTAL:</label>
          <input type="text" name="totalPayment" value={formData.totalPayment} readOnly />
        </div>

        <div className="ro-form-group">
          <label>Forma de Pago:</label>
          <select name="formaPago" value={formData.formaPago} onChange={handleChange}>
            <option value="">Seleccione...</option>
            <option value="SINPE">SINPE</option>
            <option value="CREDITO">CRÉDITO</option>
            <option value="TRANSFERENCIA BANCARIA">TRANSFERENCIA BANCARIA</option>
            <option value="PAYPAL">PAYPAL</option>
            <option value="WESTERN UNION">WESTERN UNION</option>
            <option value="EFECTIVO">EFECTIVO</option>
            <option value="TARJETA">TARJETA</option>
          </select>
        </div>

        <div className="ro-form-group">
          <label>Cliente (opcional):</label>
          <Select
            className="react-select"
            classNamePrefix="react-select"
            name="customer"
            options={clientOptions}
            value={clientOptions.find((option) => option.value === formData.customer) || null}
            onChange={(option) =>
              handleChange({
                target: { name: 'customer', value: option ? option.value : '' },
              })
            }
            placeholder="Buscar y seleccionar un cliente..."
            isClearable
          />
        </div>

        <div className="ro-form-actions">
          <button type="submit" disabled={isSubmitting}>
            {isSubmitting ? 'Registrando...' : 'Registrar Pedido'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default RegisterOrder;
